import type { TotalMonthlyPriceDisplayInfo } from '@/src/types/listingDetails.type';

export const FEES_DISCLOSURE_STATES = ['MA', 'CT'];

export type TotalMonthlyPriceListing = {
  address?: {
    state?: string | null;
  } | null;
  hasTotalMonthlyPrice?: boolean | null;
  totalMonthlyPriceDisplayInfo?: TotalMonthlyPriceDisplayInfo | null;
};

type V3TotalMonthlyPriceDisplayInfo = {
  listingTooltip?: unknown;
  modelTooltips?: unknown;
  tooltipTexts?: unknown;
};

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function normalizedString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

export function requiresFeesDisclosure(listing: TotalMonthlyPriceListing | null | undefined): boolean {
  const state = listing?.address?.state?.toUpperCase() ?? '';
  return FEES_DISCLOSURE_STATES.includes(state);
}

export function shouldShowTotalMonthlyPriceIndicator(
  listing: TotalMonthlyPriceListing | null | undefined,
): boolean {
  return listing?.hasTotalMonthlyPrice === true || requiresFeesDisclosure(listing);
}

export function getTotalMonthlyPriceTooltipText(
  displayInfo: TotalMonthlyPriceDisplayInfo | null | undefined,
  modelIds?: string | Array<string | undefined>,
): string | undefined {
  const tooltipTexts = displayInfo?.tooltipTexts;
  if (!isPlainObject(tooltipTexts)) return undefined;

  const candidateModelIds = Array.isArray(modelIds) ? modelIds : [modelIds];
  for (const modelId of candidateModelIds) {
    const normalizedModelId = modelId?.trim();
    const modelTooltip = normalizedModelId ? normalizedString(tooltipTexts[normalizedModelId]) : undefined;
    if (modelTooltip) return modelTooltip;
  }

  return normalizedString(tooltipTexts.listing);
}

export function normalizeTotalMonthlyPriceDisplayInfo(
  displayInfo: V3TotalMonthlyPriceDisplayInfo | null | undefined,
): TotalMonthlyPriceDisplayInfo | undefined {
  if (!displayInfo || !isPlainObject(displayInfo)) return undefined;

  const tooltipTexts: Record<string, string> = {};
  const legacyTooltipTexts = displayInfo.tooltipTexts;
  if (isPlainObject(legacyTooltipTexts)) {
    for (const [key, value] of Object.entries(legacyTooltipTexts)) {
      const text = normalizedString(value);
      if (key && text) tooltipTexts[key] = text;
    }
  }

  const listingTooltip = normalizedString(displayInfo.listingTooltip);
  if (listingTooltip) tooltipTexts.listing = listingTooltip;

  if (Array.isArray(displayInfo.modelTooltips)) {
    for (const item of displayInfo.modelTooltips) {
      if (!isPlainObject(item)) continue;
      const modelId = normalizedString(item.modelId);
      const text = normalizedString(item.text);
      if (modelId && text) tooltipTexts[modelId] = text;
    }
  }

  return {
    tooltipTexts,
  };
}
